"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useHookFormAction } from "@next-safe-action/adapter-react-hook-form/hooks"
import { useTranslations } from "next-intl"
import type { ComponentProps } from "react"
import { Box } from "~/components/legacy/common/box"
import { Button } from "~/components/legacy/common/button"
import { Form, FormControl, FormField } from "~/components/legacy/common/form"
import { Hint } from "~/components/legacy/common/hint"
import { Input } from "~/components/legacy/common/input"
import { cx } from "~/lib/legacy-utils"
import { subscribeToLegalUpdates } from "~/server/web/actions/subscribe-legal-updates"
import { createLegalUpdatesSchema } from "~/server/web/shared/schema"

type InputProps = ComponentProps<typeof Input>

type SubprocessorSubscribeFormProps = ComponentProps<"form"> & {
  size?: Extract<InputProps["size"], "md" | "lg">
}

export const SubprocessorSubscribeForm = ({
  className,
  size = "md",
  ...props
}: SubprocessorSubscribeFormProps) => {
  const t = useTranslations("forms.legal_updates")
  const tSchema = useTranslations("schema")

  const schema = createLegalUpdatesSchema(tSchema)
  const resolver = zodResolver(schema)

  const { form, action, handleSubmitWithAction } = useHookFormAction(
    subscribeToLegalUpdates,
    resolver,
    {
      formProps: {
        defaultValues: {
          captcha: "",
          email: "",
        },
      },

      actionProps: {
        onSettled: () => {
          form.reset()
        },
      },
    },
  )

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-base font-semibold">{t("heading")}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t("description")}</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmitWithAction}
          className={cx("flex flex-col gap-3 w-full", className)}
          noValidate
          {...props}
        >
          <FormField
            control={form.control}
            name="captcha"
            render={({ field }) => (
              <FormControl>
                <Input type="hidden" {...field} />
              </FormControl>
            )}
          />

          <Box focusWithin>
            <div className="flex w-full bg-background rounded-lg">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("email_placeholder")}
                      size={size}
                      className="flex-1 min-w-0 border-0 focus-visible:outline-none"
                      data-1p-ignore
                      {...field}
                    />
                  </FormControl>
                )}
              />

              <Button
                isPending={action.isPending}
                className={cx(
                  "shrink-0",
                  size === "lg" ? "text-sm/tight px-4 py-2 m-1" : "px-3 py-1.5 m-0.5",
                )}
                size="sm"
              >
                {t("button")}
              </Button>
            </div>
          </Box>

          {(action.result.serverError || form.formState.errors.email) && (
            <Hint className="-mt-1">
              {action.result.serverError || form.formState.errors.email?.message}
            </Hint>
          )}

          {action.result.data && <p className="text-sm text-green-600">{action.result.data}</p>}
        </form>
      </Form>
    </div>
  )
}
